import type { GridOptions } from "@/Domain/GridOptions";
import { SessionCacheService as  CacheService } from "@/shared/services/SessionCacheService";
import type {
  GridColumnProps,
  GridColumnReorderEvent,
  GridColumnResizeEvent,
} from "@progress/kendo-vue-grid";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { handleResponsePromiseAsync } from "../helpers/apiHelper";
import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
} from "@progress/kendo-data-query";

export class GridOptionsService {
  static getKey(gridName: string) {
    const main = useMainStore();
    return `${gridName}_GridOptions;${main.userLogin};${main.companyLogin}`;
  }

  static getPageSizeKey(gridName: string) {
    return `${gridName}_PageSize`;
  }

  static getDefaultOptions() {
    return {
      visibleColumns: [],
      columnWidths: {},
      columnsOrder: {},
    } as GridOptions;
  }

  static getGridOptions(gridName: string) {
    const key = this.getKey(gridName);
    return CacheService.get(key, () => this.getDefaultOptions());
  }

  static saveVisibleColumns(gridName: string, columns: GridColumnProps[]) {
    this.saveVisibleColumnStrings(
      gridName,
      columns
        .filter((c) => !c.hidden && c.title)
        .map((c) => c.title) as string[]
    );
  }

  static saveVisibleColumnStrings(gridName: string, visibleColumns: string[]) {
    const key = this.getKey(gridName);
    const options = CacheService.get(key, () => this.getDefaultOptions());
    options.visibleColumns = visibleColumns;
    CacheService.set(key, options);
  }

  static clearVisibleColumns(gridName: string) {
    const key = this.getKey(gridName);
    const options = CacheService.get(key, () => this.getDefaultOptions());
    options.visibleColumns = [];
    CacheService.set(key, options);
  }

  static saveColumnWidths(
    gridName: string,
    columnWidths: { [key: string]: number }
  ) {
    const key = this.getKey(gridName);
    const options = CacheService.get(key, () => this.getDefaultOptions());
    options.columnWidths = columnWidths;
    CacheService.set(key, options);
  }

  static saveColumnWidth(gridName: string, column: string, width: number) {
    const key = this.getKey(gridName);
    const options = CacheService.get(key, () => this.getDefaultOptions());
    options.columnWidths[column] = width;
    CacheService.set(key, options);
  }

  static clearColumnWidths(gridName: string) {
    const key = this.getKey(gridName);
    const options = CacheService.get(key, () => this.getDefaultOptions());
    options.columnWidths = {};
    CacheService.set(key, options);
  }

  static saveColumnsOrder(gridName: string, columns: GridColumnProps[]) {
    const key = this.getKey(gridName);
    const options = CacheService.get(key, () => this.getDefaultOptions());
    for (const col of columns.filter((c) => c.orderIndex !== undefined)) {
      options.columnsOrder[col.title!] = col.orderIndex!;
    }
    CacheService.set(key, options);
  }

  static clearColumnsOrder(gridName: string) {
    const key = this.getKey(gridName);
    const options = CacheService.get(key, () => this.getDefaultOptions());
    options.columnsOrder = {};
    CacheService.set(key, options);
  }

  static saveGridFilter(gridName: string, filter: CompositeFilterDescriptor) {
    const key = this.getKey(gridName);
    CacheService.set(key, filter);
  }

  static loadGridFilter(gridName: string) {
    const key = this.getKey(gridName);
    return CacheService.get(key, () => {
      return {
        logic: "and",
        filters: [{} as FilterDescriptor],
      } as CompositeFilterDescriptor;
    });
  }

  static savePageSizeAsync(gridName: string, pageSize: number) {
    const propertyName = this.getPageSizeKey(gridName);
    const main = useMainStore();
    const requestParams = {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      propertyName: propertyName,
      propertyValue: pageSize.toString(),
    };
    return api.get("SetPropertyValues", requestParams);
  }

  static clearPageSizeAsync(gridName: string) {
    return this.savePageSizeAsync(gridName, 0);
  }

  static async getPageSizeAsync(gridName: string) {
    try {
      const propertyName = this.getPageSizeKey(gridName);
      const main = useMainStore();
      const requestParams = {
        userLogin: main.userLogin,
        companyLogin: main.companyLogin,
        propertyName: propertyName,
      };
      let pageSize: number | null = null;
      await handleResponsePromiseAsync(
        api.get("GetPropertyValues", requestParams),
        (data) => {
          if (data && data.length > 0) pageSize = parseInt(data[0]);
        },
        "GridOptionsService",
        "getPageSizeAsync"
      );
      if (pageSize != null && pageSize <= 0) pageSize = null;
      return pageSize;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  static loadGridOptions(gridName: string, columns: GridColumnProps[]) {
    const gridOptions = this.getGridOptions(gridName);
    if (!gridOptions) return;

    // Load visible columns
    const cols = columns.filter((c) => c.title);
    if (gridOptions.visibleColumns.length > 0) {
      cols.forEach((col) => {
        col.hidden = !gridOptions.visibleColumns.find((vc) => vc === col.title);
      });
    }

    // Load column widths
    if (gridOptions.columnWidths) {
      cols.forEach((col) => {
        const columnWidth = gridOptions.columnWidths[col.title!];
        if (columnWidth) {
          col.width = columnWidth;
        }
      });
    }

    // Load column order
    if (gridOptions.columnsOrder) {
      cols.forEach((col) => {
        const orderIndex = gridOptions.columnsOrder[col.title!];
        if (orderIndex !== undefined) {
          col.orderIndex = orderIndex;
        }
      });
    }
  }

  static getResizedColumn(
    e: GridColumnResizeEvent,
    columns: GridColumnProps[]
  ) {
    // First try to find column by the order index
    // Filters out any hidden columns from the columns array
    // Then filters out any columns that have a duplicate orderIndex value.
    const orderedCols = columns
      .filter((c) => !c.hidden)
      .filter(
        (c, i, a) =>
          !c.orderIndex ||
          a.map((e) => e.orderIndex).indexOf(c.orderIndex) === i
      )
      .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
    const colByOrderIndex = orderedCols[e.index];
    if (colByOrderIndex) return colByOrderIndex;

    // If not found, try to find column by the index
    return columns.filter((c) => !c.hidden)[e.index];
  }

  // 18152: should not be able to move the 'select' column nor the 'detail' column
  static setSelectDetailsColumnOrder(e: GridColumnReorderEvent) {
    const selectedColumn = e.columns.find((c) => c.field === "selected");
    const detailsColumn = e.columns.find((c) => c.cell === "DetailsCell");
    if (!selectedColumn && !detailsColumn) {
      return;
    }
    const haveBothCols = selectedColumn && detailsColumn;
    e.columns
      .filter((c) => c.orderIndex == 0 || (haveBothCols && c.orderIndex == 1))
      .forEach((c) => (c.orderIndex = haveBothCols ? 2 : 1));
    if (selectedColumn) selectedColumn.orderIndex = 0;
    if (detailsColumn) detailsColumn.orderIndex = selectedColumn ? 1 : 0;
  }
}
